import { Period } from '@js-joda/core';
import { ReactElement } from 'react';

import { DocplannerHealthcareProviderConfiguration } from '@dreryk/egabinet-docplanner-frontend-api';

import { createTranslatedText, dateTimeFormatter } from '@dreryk/react-common';
import { Heading, LabeledValue, StyledFlex, TextView } from '@dreryk/react-components';

import { docplannerTranslationKeys, integrationTranslationKeys } from '../translations';

import { DocplannerUpdateClientSecret } from './updateClientSecret/DocplannerUpdateClientSecret';
import { DocplannerUpdateSynchronizationDaysCount } from './updateSynchronizationDaysCount/DocplannerUpdateSynchronizationDaysCount';

type IntegrationInformationSectionProps = {
   readonly clientId: string;
   readonly docplannerInstitutionName: string;
   readonly lastValidSynchronization: Date;
   readonly synchronizationDaysCount: Period;
   readonly onDocplannerConfigurationUpdated: (
      currentDocplannerConfiguration: DocplannerHealthcareProviderConfiguration
   ) => void;
};

const VISIBLE_API_KEY_CHARACTERS = 3;

const obfuscateApiKey = (apiKey: string): string => {
   const prefix = apiKey.slice(0, VISIBLE_API_KEY_CHARACTERS);
   const suffix = apiKey.slice(apiKey.length - VISIBLE_API_KEY_CHARACTERS, apiKey.length);
   return `${prefix}***${suffix}`;
};

export const DocplannerIntegrationInformationSection = (props: IntegrationInformationSectionProps): ReactElement => {
   const displayableClientId = obfuscateApiKey(props.clientId);
   const synchronizationDaysCount = props.synchronizationDaysCount.days();
   return (
      <section>
         <Heading
            content={integrationTranslationKeys.integrationConfiguration}
            type={'h2'}
            testId={'docplannerIntegration'}
         />
         <StyledFlex gap={'110px'}>
            <StyledFlex direction={'column'} gap={'8px'}>
               <TextView testId={'apiKey'} label={integrationTranslationKeys.apiKey}>
                  {displayableClientId}
               </TextView>
               <DocplannerUpdateClientSecret apiKey={displayableClientId} />
            </StyledFlex>
            <StyledFlex direction={'column'} gap={'8px'}>
               <LabeledValue
                  testId={'docplannerInstitutionName'}
                  label={docplannerTranslationKeys.docplannerInstitutionName}
                  value={createTranslatedText(props.docplannerInstitutionName)}
               />
               <LabeledValue
                  testId={'lastValidSynchronization'}
                  label={integrationTranslationKeys.lastValidSynchronization}
                  value={createTranslatedText(dateTimeFormatter(props.lastValidSynchronization))}
               />
               <StyledFlex gap={'8px'}>
                  <LabeledValue
                     testId={'synchronizationDaysCount'}
                     label={integrationTranslationKeys.synchronizationDaysCount}
                     value={createTranslatedText(synchronizationDaysCount.toString())}
                  />
                  <DocplannerUpdateSynchronizationDaysCount
                     currentSynchronizationDaysCount={synchronizationDaysCount}
                     onDaysCountUpdated={props.onDocplannerConfigurationUpdated}
                  />
               </StyledFlex>
            </StyledFlex>
         </StyledFlex>
      </section>
   );
};
